/**
 * S3AssetUploader - Frontend service for uploading assets to S3
 * @date_of_change: 2025-10-01 - Created S3 upload service to replace base64 WebSocket broadcasts
 *
 * This service replaces the inefficient base64 data URL system with proper S3 uploads.
 * Instead of sending 7MB images through WebSocket messages, we upload to S3 and share URLs.
 *
 * ARCHITECTURE:
 * 1. Sign upload with backend to get S3 signed URL
 * 2. Upload file directly to S3 using signed URL
 * 3. Finalize upload with backend to get CDN URL
 * 4. Return lightweight S3 URL for broadcasting
 */

import apiFetch from '../utils/apiFetch';
import { normalizeUploadedAsset } from '../utils/uploadAssetUrlResolver';

const SUPPORTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];

const parseApiError = async (response, fallbackMessage) => {
  try {
    const payload = await response.clone().json();
    return payload?.message || payload?.error || fallbackMessage;
  } catch (_jsonError) {
    try {
      const text = await response.text();
      if (text && !text.includes('<!DOCTYPE')) {
        return text;
      }
    } catch (_textError) {
      // Ignore text parsing errors and fall back to the generic message below.
    }
  }

  return fallbackMessage;
};

const abortPendingUpload = async (assetId) => {
  if (!assetId) {
    return;
  }

  try {
    await apiFetch('/api/s3-uploads/abort', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ assetId }),
      requireAuth: true
    });
  } catch (error) {
    console.warn('[S3AssetUploader] Failed to abort pending upload:', error);
  }
};

const uploadFileToSignedUrl = async (putUrl, file) => {
  try {
    const uploadResponse = await fetch(putUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });

    if (!uploadResponse.ok) {
      throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
    }
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error('The image vault did not accept the upload. This usually means the S3 bucket CORS rules still need to allow this site.');
    }
    throw error;
  }
};

const finalizeUpload = async (assetId) => {
  let lastError = null;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    const response = await apiFetch('/api/s3-uploads/finalize', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ assetId }),
      requireAuth: true
    });

    if (response.ok) {
      return response.json();
    }

    const message = await parseApiError(response, `Finalize upload failed: HTTP ${response.status}`);
    lastError = new Error(message);

    if (attempt < 1) {
      await new Promise((resolve) => setTimeout(resolve, 350));
    }
  }

  throw lastError || new Error('Finalize upload failed.');
};

/**
 * Upload a file to S3 and return the CDN URL
 * @param {File} file - The image file to upload
 * @param {string} assetType - Type of asset ('map', 'token', 'character', 'monster')
 * @returns {Promise<{url: string, assetId: string}>} S3 URL and asset ID
 */
export const uploadAssetToS3 = async (file, assetType = 'token') => {
  let assetId = null;
  try {
    console.log('=== FRONTEND S3 UPLOAD DEBUG ===');
    console.log('[S3AssetUploader] Starting S3 upload for:', file.name, 'type:', assetType);
    console.log('[S3AssetUploader] File size:', file.size, 'bytes');
    console.log('[S3AssetUploader] File type:', file.type);

    const { assertAssetUploadAllowed } = await import('./EntitlementService.js');
    await assertAssetUploadAllowed({ fileSize: file.size });

    // Step 1: Sign the upload with backend
    const signResponse = await apiFetch('/api/s3-uploads/sign', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: assetType,
        mime: file.type,
        size: file.size,
        ext: file.name.split('.').pop(),
        originalName: file.name
      }),
      requireAuth: true
    });

    if (!signResponse.ok) {
      const errorMessage = await parseApiError(signResponse, `Sign upload failed: HTTP ${signResponse.status}`);
      throw new Error(errorMessage);
    }

    const signPayload = await signResponse.json();
    assetId = signPayload.assetId;
    const { key, putUrl } = signPayload;
    console.log('[S3AssetUploader] Got signed URL for:', key);

    // Step 2: Upload directly to S3 using signed URL
    await uploadFileToSignedUrl(putUrl, file);
    console.log('[S3AssetUploader] File uploaded to S3 successfully');

    // Step 3: Finalize upload with backend
    const finalResult = await finalizeUpload(assetId);
    const normalizedAsset = normalizeUploadedAsset(finalResult?.asset || {});
    console.log('[S3AssetUploader] Upload finalized, canonical URL:', normalizedAsset.url);

    return {
      url: normalizedAsset.url,
      proxyUrl: normalizedAsset.proxyUrl,
      canonicalUrl: normalizedAsset.canonicalUrl,
      fallbackUrl: normalizedAsset.fallbackUrl,
      assetId: finalResult.asset.id,
      key: normalizedAsset.key || finalResult.asset.key,
      type: finalResult.asset.type,
      size: finalResult.asset.size
    };

  } catch (error) {
    console.error('[S3AssetUploader] Upload failed:', error);
    if (assetId && /accept the upload|s3 upload failed/i.test(String(error?.message || ''))) {
      await abortPendingUpload(assetId);
    }
    throw error;
  }
};

/**
 * Validate file before upload
 * @param {File} file - File to validate
 * @param {string} assetType - Intended asset type ('map' or 'token')
 * @returns {boolean} Whether file is valid for upload
 */
export const validateAssetFile = (file, assetType = 'token') => {
  // Check file type
  if (!file.type.startsWith('image/')) {
    throw new Error('Only image files are supported');
  }

  // Check file size (max 50MB for maps, 10MB for tokens)
  const maxSize = assetType === 'map' ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
  if (file.size > maxSize) {
    const maxSizeMB = maxSize / (1024 * 1024);
    throw new Error(`File too large. Maximum size: ${maxSizeMB}MB`);
  }

  // Check supported formats
  if (!SUPPORTED_IMAGE_TYPES.includes(file.type)) {
    throw new Error('Unsupported file format. Supported: PNG, JPEG, WebP, GIF');
  }

  return true;
};

/**
 * Create asset object from S3 upload result
 * @param {Object} uploadResult - Result from uploadAssetToS3
 * @param {File} file - Original file
 * @param {string} layer - Target layer
 * @returns {Object} Asset object compatible with BattleRoom
 */
export const createAssetFromS3Upload = (uploadResult, file, layer) => {
  const assetType = getAssetType(layer);

  return {
    id: uploadResult.assetId,
    name: file.name,
    type: assetType,
    layer: layer,
    url: uploadResult.url, // S3 CDN URL instead of base64
    s3Key: uploadResult.key,
    width: assetType === 'map' ? 800 : 100,
    height: assetType === 'map' ? 600 : 100,
    x: 100,
    y: 100,
    uploadedAt: new Date().toISOString(),
    fileSize: uploadResult.size
  };
};

/**
 * Get asset type from layer
 * @param {string} layer - Active layer
 * @returns {string} Asset type
 */
const getAssetType = (layer) => {
  switch(layer) {
    case 'map':
      return 'map';
    case 'token':
    case 'gamemaster':
    default:
      return 'token';
  }
};

const S3AssetUploader = {
  uploadAssetToS3,
  validateAssetFile,
  createAssetFromS3Upload
};

export default S3AssetUploader;
